import NcrReportFields from "./NcrReportFields";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import NcrReportGroups from "./NcrReportGroups";
import NcrPeopleFields from "./NcrPeopleFields";
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  LinearProgress,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "components/NoScrollDialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import LoadingButton from "components/LoadingButton";
import { DEV_ANSWER_TYPE_ID } from "const";
import { useLanguage } from "contexts/language.context";
import * as React from "react";
import dayjs from "dayjs";
import ncrService from "services/ncr-service";
import { hasNcrTextErrors, hasNcrHeaderErrors, ncrTextFieldProps } from "../ncrTextValidation";

const DEFAULT_AUDIT_PAGE_SIZE = 5;

function createRequestId() {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export default function GenerateNcrDialog({
  isOpen,
  onSubmit,
  onClose,
  sourceAuditId,
  sourceNcr,
}) {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [options, setOptions] = React.useState({
    audits: null,
  });
  const [audit, setAudit] = React.useState(null);
  const [showAllKpis, setShowAllKpis] = React.useState(false);
  const [rows, setRows] = React.useState([]);
  const [error, setError] = React.useState("");
  const saving = React.useRef(false);
  const requestId = React.useRef(null);
  const auditRequest = React.useRef(0);
  const searchRequest = React.useRef(0);
  const nextGroupId = React.useRef(1);
  const [auditSelection, setAuditSelection] = React.useState(null);
  const [auditLoading, setAuditLoading] = React.useState(false);
  const [auditSearch, setAuditSearch] = React.useState("");
  const [auditPageSize, setAuditPageSize] = React.useState(
    DEFAULT_AUDIT_PAGE_SIZE,
  );
  const [auditPageInput, setAuditPageInput] = React.useState("0");
  const [auditPage, setAuditPage] = React.useState({
    term: "",
    skip: 0,
    count: 0,
    loading: false,
    error: "",
  });
  const fixedAuditId = sourceAuditId || sourceNcr?.auditId;
  const [ncr, setNcr] = React.useState({
    referenceClause: "",
    statusId: "",
    repetitionTime: "",
    personInCharge: "",
    teamMember: "",
    branchGroupDescription: "",
    isRepeated: false,
    isReprinted: false,
    estimatedClosureDate: null,
  });
  const { label } = useLanguage();

  async function loadAudits(term = "", skip = 0, take = auditPageSize) {
    const request = ++searchRequest.current;
    if (options.audits?.length) setAuditPageInput(String(currentAuditPage));
    setAuditPage((current) => ({
      ...current,
      term,
      requestedSkip: skip,
      loading: true,
      error: "",
    }));
    try {
      const [response, result] = await ncrService.getEligibleAudits({
        autocomplete: term,
        skip,
        take,
      });
      if (request !== searchRequest.current) return;
      if (!response.ok)
        throw new Error(
          result?.error?.message ||
            result?.error ||
            label("unable-to-load-audits") ||
            "Unable to load audits",
        );
      if (skip > 0 && skip >= result.count) {
        return loadAudits(
          term,
          Math.max(0, Math.floor((result.count - 1) / take) * take),
          take,
        );
      }
      setOptions({ audits: result.data });
      setAuditPageInput(
        result.count ? String(Math.floor(skip / take) + 1) : "0",
      );
      setAuditPage({
        term,
        skip,
        count: result.count,
        loading: false,
        error: "",
      });
    } catch (failure) {
      if (request === searchRequest.current) {
        setAuditPage((current) => ({
          ...current,
          loading: false,
          error:
            failure.message ||
            label("unable-to-load-audits") ||
            "Unable to load audits",
        }));
      }
    }
  }

  const auditTotalPages = Math.ceil(auditPage.count / auditPageSize);
  const currentAuditPage = auditPage.count
    ? Math.floor(auditPage.skip / auditPageSize) + 1
    : 0;
  const auditPagingDisabled =
    loading || auditPage.loading || Boolean(auditPage.error);
  function goToAuditPage() {
    if (auditPagingDisabled || !auditTotalPages) return;
    const requested = auditPageInput.trim()
      ? Number(auditPageInput)
      : currentAuditPage;
    const page = Math.min(
      auditTotalPages,
      Math.max(
        1,
        Number.isFinite(requested) ? Math.trunc(requested) : currentAuditPage,
      ),
    );
    setAuditPageInput(String(page));
    if (page !== currentAuditPage)
      loadAudits(auditPage.term, (page - 1) * auditPageSize);
  }

  React.useEffect(() => {
    if (!isOpen || !open || fixedAuditId) return;
    const timer = setTimeout(() => loadAudits(auditSearch, 0), 250);
    return () => {
      clearTimeout(timer);
      searchRequest.current++;
    };
  }, [isOpen, open, fixedAuditId, auditSearch, auditPageSize]);

  React.useEffect(() => {
    if (!isOpen) return;
    requestId.current = null;
    setOpen(true);
    setLoading(false);
    setAudit(null);
    setShowAllKpis(false);
    setRows([]);
    setError("");
    setAuditSelection(null);
    setAuditLoading(false);
    nextGroupId.current = 1;
    setAuditSearch("");
    setAuditPageSize(DEFAULT_AUDIT_PAGE_SIZE);
    setAuditPageInput("0");
    setOptions({ audits: [] });
    setAuditPage({ term: "", skip: 0, count: 0, loading: false, error: "" });
    setNcr({
      referenceClause: "",
      statusId: "",
      repetitionTime: "",
      personInCharge: "",
      teamMember: "",
      branchGroupDescription: "",
      isRepeated: false,
      isReprinted: false,
      estimatedClosureDate: null,
    });
    if (sourceNcr) {
      const fields = [
        "referenceClause",
        "personInCharge",
        "teamMember",
        "branchGroupDescription",
        "isRepeated",
        "repetitionTime",
      ];
      setNcr((current) => ({
        ...current,
        ...Object.fromEntries(
          fields
            .filter((key) => sourceNcr[key] != null)
            .map((key) => [key, sourceNcr[key]]),
        ),
        estimatedClosureDate: sourceNcr.estimatedClosureDate
          ? dayjs(sourceNcr.estimatedClosureDate)
          : null,
      }));
    }
    if (fixedAuditId) selectAudit(fixedAuditId);
    return () => {
      auditRequest.current++;
      searchRequest.current++;
    };
  }, [isOpen, sourceAuditId, sourceNcr?.id]);

  // ponytail: the audit tree nests pillars up to 3 deep, and the shared
  // filteredPillars helper is top-level only — so walk it here.
  function collectKpis(pillars) {
    return (pillars || []).flatMap((pillar) => [
      ...(pillar.kpis || []),
      ...collectKpis(pillar.pillars),
    ]);
  }

  function buildRows(auditData, includeNonDeviated) {
    const kpis = auditData?.auditKpis
      ? auditData.auditKpis.map((row) => ({
          ...row.kpi,
          id: row.kpiId,
          auditKpis: [row],
        }))
      : collectKpis(auditData?.pillars);
    return kpis
      .filter((kpi) => kpi.auditKpis?.length)
      .filter(
        (kpi) =>
          includeNonDeviated ||
          kpi.auditKpis[0].kpiValueTypeAnswerId == DEV_ANSWER_TYPE_ID,
      )
      .map((kpi, index) => {
        const auditKpi = kpi.auditKpis[0];

        // Corrective action becomes an editable NCR copy; source text/photos stay live.
        return {
          auditKpiId: auditKpi.id,
          kpiId: kpi.id,
          kpiReferenceCode: kpi.referenceCode,
          nonConformityDescription: auditKpi.kpiDeviation?.description || "",
          correctiveAction: auditKpi.kpiAction?.description || "",
          rootCause: "",
          remarks: "",
          attachments: (auditKpi.attachments || [])
            .filter((item) => item.url)
            .map((item) => ({
              url: item.url,
              remarks: item.remarks,
              type: item.type,
            })),
          groupId: index + 1,
          isDeviated: auditKpi.kpiValueTypeAnswerId == DEV_ANSWER_TYPE_ID,
          issued: false,
          selected: auditKpi.kpiValueTypeAnswerId == DEV_ANSWER_TYPE_ID,
        };
      });
  }

  async function selectAudit(auditId) {
    const request = ++auditRequest.current;
    setAuditSelection(auditId);
    setAuditLoading(Boolean(auditId));
    nextGroupId.current = 1;
    setAudit(null);
    setRows([]);
    setError("");

    if (!sourceNcr)
      setNcr((current) => ({
        ...current,
        personInCharge: "",
        teamMember: "",
      }));

    if (!auditId) return;

    let response, data;
    try {
      [response, data] = await ncrService.getSourceAudit(auditId);
    } catch (failure) {
      if (request === auditRequest.current) {
        setAuditLoading(false);
        setError(
          failure.message ||
            label("unable-to-load-audit") ||
            "Unable to load audit",
        );
      }
      return;
    }
    if (request !== auditRequest.current) return;
    setAuditLoading(false);
    if (!response.ok || !data) {
      setError(
        data?.error?.message ||
          data?.error ||
          label("unable-to-load-audit") ||
          "Unable to load audit",
      );
      return;
    }
    if (![2, 4, 7].includes(Number(data.statusId))) {
      setError(
        label("source-audit-must-be-completed") ||
          "The source audit must be completed",
      );
      return;
    }
    if (response.ok && data) {
      setAudit(data);
      if (!sourceNcr)
        setNcr((ncr) => ({
          ...ncr,
          branchGroupDescription: data.branch?.branchGroup?.name || "",
        }));
      const sourceRows = buildRows(data, true);
      const sourceById = new Map(
        sourceRows.map((row) => [row.auditKpiId, row]),
      );
      setRows(
        sourceNcr
          ? (sourceNcr.details || []).map((detail) => {
              const current = sourceById.get(detail.auditKpiId);
              return {
                ...detail,
                kpiReferenceCode: current?.kpiReferenceCode || "",
                nonConformityDescription:
                  current?.nonConformityDescription || "",
                attachments: current?.attachments || [],
                selected: true,
                issued: false,
                isDeviated: true,
                groupId: 1,
              };
            })
          : sourceRows,
      );
      nextGroupId.current = sourceNcr ? 2 : sourceRows.length + 1;
    }
  }

  function toggleShowAllKpis(value) {
    setShowAllKpis(value);
  }

  const toggleRow = React.useCallback((auditKpiId) => {
    setRows((rows) =>
      rows.map((row) =>
        row.auditKpiId === auditKpiId && !row.issued
          ? { ...row, selected: !row.selected }
          : row,
      ),
    );
  }, []);

  function handleClose() {
    auditRequest.current++;
    searchRequest.current++;
    setOpen(false);
    onClose?.();
  }

  const selectedRows = React.useMemo(
    () => rows.filter((row) => row.selected),
    [rows],
  );
  const selectedRowsHaveErrors = React.useMemo(
    () => selectedRows.some(hasNcrTextErrors),
    [selectedRows],
  );
  const visibleRows = React.useMemo(
    () => rows.filter((row) => showAllKpis || row.isDeviated),
    [rows, showAllKpis],
  );
  const hiddenSelectedCount = selectedRows.filter(
    (row) => !showAllKpis && !row.isDeviated,
  ).length;
  const groups = React.useMemo(() => {
    const grouped = new Map();
    rows.forEach((row) => {
      if (!grouped.has(row.groupId)) grouped.set(row.groupId, []);
      grouped.get(row.groupId).push(row);
    });
    return grouped;
  }, [rows]);
  const selectedGroups = React.useMemo(
    () =>
      Array.from(groups.values())
        .map((group) => group.filter((row) => row.selected))
        .filter((group) => group.length),
    [groups],
  );

  const groupRows = React.useCallback((ids, destination) => {
    const groupId = Number(destination);
    if (!rows.some((row) => row.groupId === groupId && !row.issued)) return;
    const members = new Set(ids);
    const separateGroups = new Map(rows
      .filter((row) => !row.issued && row.groupId === groupId && !members.has(row.auditKpiId))
      .map((row) => [row.auditKpiId, nextGroupId.current++]));
    setRows((current) => current.map((row) => {
      if (row.issued) return row;
      if (members.has(row.auditKpiId)) return { ...row, groupId };
      if (separateGroups.has(row.auditKpiId)) return { ...row, groupId: separateGroups.get(row.auditKpiId) };
      return row;
    }));
  }, [rows]);

  return (
    <Dialog
      fullWidth
      maxWidth="xl"
      open={open}
      onClose={loading ? undefined : handleClose}
      PaperProps={{
        component: "form",
        onSubmit: async (event) => {
          event.preventDefault();

          if (
            !audit ||
            !selectedRows.length ||
            saving.current ||
            hasNcrHeaderErrors(ncr) ||
            selectedRowsHaveErrors
          )
            return;

          saving.current = true;
          setLoading(true);
          setError("");
          try {
            const payload = {
              statusId: Number(ncr.statusId),
              repetitionTime: ncr.repetitionTime === "" || ncr.repetitionTime == null ? null : Number(ncr.repetitionTime),
              auditId: audit.id,
              referenceClause: ncr.referenceClause,
              personInCharge: ncr.personInCharge,
              teamMember: ncr.teamMember,
              branchGroupDescription: ncr.branchGroupDescription,
              estimatedClosureDate: ncr.estimatedClosureDate
                ? ncr.estimatedClosureDate.$d
                : null,
              isRepeated: ncr.isRepeated,
              isReprinted: ncr.isReprinted,
              groups: selectedGroups.map((group) =>
                group.map((row, index) => ({
                  rowIndex: index + 1,
                  auditKpiId: row.auditKpiId,
                  kpiId: row.kpiId,
                  kpiReferenceCode: row.kpiReferenceCode,
                  nonConformityDescription: row.nonConformityDescription,
                  rootCause: row.rootCause,
                  correctiveAction: row.correctiveAction,
                  remarks: row.remarks,
                })),
              ),
            };
            if (!requestId.current) requestId.current = createRequestId();
            const result = await onSubmit({
              ...payload,
              requestId: requestId.current,
            });
            if (!result?.success) {
              const reason = result?.error?.message || result?.error;
              throw new Error(
                typeof reason === "string"
                  ? reason
                  : label("ncr-generation-failed") || "NCR creation failed.",
              );
            }
            handleClose();
          } catch (failure) {
            // One transaction created everything or nothing; no partial marking.
            setError(failure.message);
          } finally {
            saving.current = false;
            setLoading(false);
          }
        },
      }}
    >
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Typography component="h2" variant="h6">
          {label("generate-ncr-button") || "Generate NCR"}
        </Typography>
        {audit ? (
          <Alert severity="info" icon={false} sx={{ mt: 1, py: 0.5 }}>
            <Typography variant="body2" fontWeight="medium" sx={{ overflowWrap: "anywhere" }}>
              {audit.description || label("audit-singular") || "Audit"} · {audit.branch?.name || "—"}
            </Typography>
          </Alert>
        ) : null}
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        <Box
          component="fieldset"
          disabled={loading}
          sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
        >
          <Grid container rowSpacing={0.75} columnSpacing={1} sx={{ pt: 0.5 }}>
            <Grid item xs={12}>
              <Typography component="h3" variant="h6" sx={{ mt: 0, mb: 0 }}>
                {label("source-audit") || "Source audit"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {fixedAuditId ? (
                <TextField
                  fullWidth
                  label={label("audit-singular") || "Audit"}
                  value={
                    audit?.description ||
                    (auditSelection ? `#${auditSelection}` : "")
                  }
                  disabled
                />
              ) : (
                <Box>
                  <TextField
                    fullWidth
                    label={
                      label("ncr-search-audits") ||
                      "Search audits by description or branch"
                    }
                    value={auditSearch}
                    disabled={loading}
                    onChange={(event) => {
                      searchRequest.current++;
                      setAuditSearch(event.target.value);
                      setOptions({ audits: [] });
                      setAuditPage((current) => ({
                        ...current,
                        skip: 0,
                        loading: true,
                        error: "",
                      }));
                    }}
                  />
                  {auditPage.loading && !options.audits?.length ? (
                    <LinearProgress
                      aria-label={
                        label("ncr-loading-audits") || "Loading audits"
                      }
                      sx={{ mt: 1 }}
                    />
                  ) : null}
                  {auditPage.error ? (
                    <Alert
                      severity="error"
                      sx={{ mt: 1 }}
                      action={
                        <Button
                          type="button"
                          disabled={loading}
                          onClick={() =>
                            loadAudits(auditPage.term, auditPage.requestedSkip ?? auditPage.skip)
                          }
                        >
                          {label("ncr-retry") || "Retry"}
                        </Button>
                      }
                    >
                      {auditPage.error}
                    </Alert>
                  ) : null}
                  {options.audits?.length || (!auditPage.loading && !auditPage.error) ? (
                    <Box
                      role="radiogroup"
                      aria-busy={auditPage.loading}
                      aria-label={
                        label("ncr-eligible-audits") || "Eligible audits"
                      }
                      sx={{ mt: 1 }}
                    >
                      {(options.audits || []).map((item) => (
                        <FormControlLabel
                          key={item.id}
                          sx={{
                            display: "flex",
                            m: 0,
                            py: 0.5,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            overflowWrap: "anywhere",
                          }}
                          control={
                            <Radio
                              checked={auditSelection === item.id}
                              disabled={loading || auditPage.loading}
                              onChange={() => selectAudit(item.id)}
                              name="source-audit"
                            />
                          }
                          label={
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns:
                                  "minmax(0, 1fr) minmax(0, auto) minmax(0, auto)",
                                columnGap: 1,
                                alignItems: "baseline",
                                width: "100%",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ minWidth: 0 }}
                              >
                                {item.description ||
                                  `${label("audit-singular") || "Audit"} #${item.id}`}
                              </Typography>
                              <Typography variant="caption">
                                {item.branch?.name || "—"}
                              </Typography>
                              <Typography variant="caption">
                                {item.date
                                  ? dayjs(item.date).format("DD/MM/YYYY")
                                  : "—"}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                      {!options.audits?.length ? (
                        <Typography variant="body2" sx={{ py: 1 }}>
                          {label("ncr-no-audits") ||
                            "No completed audits match your search."}
                        </Typography>
                      ) : null}
                    </Box>
                  ) : null}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      alignItems: "center",
                      justifyContent: "space-between",
                      mt: 0.5,
                      "& .MuiOutlinedInput-root": { height: 32 },
                      "& .MuiOutlinedInput-input": {
                        height: "18px",
                        padding: "6px 10px !important",
                      },
                    }}
                  >
                    <TextField
                      select
                      size="small"
                      label={label("ncr-rows-per-page") || "Rows per page"}
                      value={auditPageSize}
                      disabled={loading}
                      sx={{ width: 120 }}
                      onChange={(event) => {
                        searchRequest.current++;
                        setAuditPageSize(Number(event.target.value));
                        setAuditPageInput("1");
                        setOptions({ audits: [] });
                        setAuditPage((current) => ({
                          ...current,
                          skip: 0,
                          loading: true,
                          error: "",
                        }));
                      }}
                    >
                      {[5, 10, 15].map((size) => (
                        <MenuItem key={size} value={size}>
                          {size}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Box
                      sx={{ display: "flex", gap: 0.5, alignItems: "center" }}
                    >
                      <IconButton
                        type="button"
                        size="small"
                        sx={{ width: 32, height: 32 }}
                        aria-label={
                          label("ncr-previous-page") || "Previous page"
                        }
                        disabled={auditPagingDisabled || currentAuditPage <= 1}
                        onClick={() =>
                          loadAudits(
                            auditPage.term,
                            Math.max(0, auditPage.skip - auditPageSize),
                          )
                        }
                      >
                        <ChevronLeft />
                      </IconButton>
                      <TextField
                        type="number"
                        size="small"
                        value={auditPageInput}
                        disabled={auditPagingDisabled || !auditTotalPages}
                        sx={{ width: 56 }}
                        inputProps={{
                          min: auditTotalPages ? 1 : 0,
                          max: auditTotalPages,
                          "aria-label": label("ncr-page") || "Page",
                        }}
                        onChange={(event) =>
                          setAuditPageInput(event.target.value)
                        }
                        onBlur={goToAuditPage}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            goToAuditPage();
                          } else if (event.key === "Escape")
                            setAuditPageInput(String(currentAuditPage));
                        }}
                      />
                      <Typography
                        variant="body2"
                        role="status"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        / {auditTotalPages}
                      </Typography>
                      <IconButton
                        type="button"
                        size="small"
                        sx={{ width: 32, height: 32 }}
                        aria-label={label("ncr-next-page") || "Next page"}
                        disabled={
                          auditPagingDisabled ||
                          currentAuditPage >= auditTotalPages
                        }
                        onClick={() =>
                          loadAudits(
                            auditPage.term,
                            auditPage.skip + auditPageSize,
                          )
                        }
                      >
                        <ChevronRight />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              )}
              {auditLoading ? (
                <LinearProgress
                  aria-label={
                    label("ncr-loading-source") || "Loading selected audit"
                  }
                  sx={{ mt: 1 }}
                />
              ) : null}
              {!audit && !auditLoading && auditSelection ? (
                <Button
                  type="button"
                  disabled={loading}
                  onClick={() => selectAudit(auditSelection)}
                >
                  {label("ncr-retry-source") || "Retry selected audit"}
                </Button>
              ) : null}
            </Grid>
            <Grid item xs={12}>
              <Typography component="h3" variant="h6" sx={{ mt: 0.75, mb: 0 }}>
                {label("report-information") || "Report information"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="referenceClause"
                name="referenceClause"
                {...ncrTextFieldProps("referenceClause", ncr?.referenceClause)}
                label={label("reference-clause-singular") || "Reference clause"}
                value={ncr.referenceClause}
                onChange={(e) => {
                  setNcr({ ...ncr, referenceClause: e.target.value });
                }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                id="branchGroupDescription"
                name="branchGroupDescription"
                {...ncrTextFieldProps(
                  "branchGroupDescription",
                  ncr?.branchGroupDescription,
                )}
                label={
                  label("branch-group-description-singular") ||
                  "Description of branch group"
                }
                value={ncr.branchGroupDescription}
                onChange={(e) => {
                  setNcr({ ...ncr, branchGroupDescription: e.target.value });
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={
                    label("estimated-closure-date-singular") ||
                    "Estimated closure date"
                  }
                  value={ncr.estimatedClosureDate}
                  onChange={(value) => {
                    setNcr({ ...ncr, estimatedClosureDate: value });
                  }}
                  sx={{ width: "100%" }}
                  format="DD/MM/YYYY"
                />
              </LocalizationProvider>
            </Grid>

            <NcrReportFields value={ncr} onChange={setNcr} />

            <Grid item xs={12}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <Box>
                  <FormControlLabel
                    label={label("ncr-repeated-label") || "Repeated deviation"}
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={!!ncr.isRepeated}
                        onChange={(e) =>
                          setNcr({ ...ncr, isRepeated: e.target.checked })
                        }
                      />
                    }
                  />
                  <Typography variant="caption" component="p" sx={{ mb: 1 }}>
                    {label("ncr-repeated-help") ||
                      "Mark this when the deviation has occurred again."}
                  </Typography>
                </Box>
                <Box>
                  <FormControlLabel
                    label={label("ncr-reprinted-label") || "Reprinted report"}
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={!!ncr.isReprinted}
                        onChange={(e) =>
                          setNcr({ ...ncr, isReprinted: e.target.checked })
                        }
                      />
                    }
                  />
                  <Typography variant="caption" component="p" sx={{ mb: 1 }}>
                    {label("ncr-reprinted-help") ||
                      "Manually indicates a reprinted copy. Printing does not increase the repetition count."}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography component="h3" variant="h6" sx={{ mt: 0.75, mb: 0 }}>
                {label("people-heading") || "People"}
              </Typography>
            </Grid>
            <NcrPeopleFields
              isOpen={isOpen}
              auditId={audit?.id}
              disabled={loading}
              value={ncr}
              onChange={setNcr}
            />

            <Grid item xs={12}>
              <Typography component="h3" variant="h6" sx={{ mt: 0.75, mb: 0 }}>
                {label("kpi-details") || "KPI details"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  pt: 0.5,
                }}
              >
                {/* Hidden 2026-10-08 per client: non-deviated NCR creation off.
                    Logic kept — delete this guard to restore. */}
                {false && (
                  <FormControlLabel
                    label={
                      label("show-non-deviated-kpis-label") ||
                      "Show non-deviated KPIs"
                    }
                    control={
                      <Switch
                        checked={showAllKpis}
                        onChange={(e) => {
                          toggleShowAllKpis(e.target.checked);
                        }}
                      />
                    }
                  />
                )}
              </Box>

              {audit && (
                <Typography variant="body2" role="status" sx={{ mb: 1 }}>
                  {`${selectedRows.length} ${label(selectedRows.length === 1 ? "ncr-kpi-selected" : "ncr-kpis-selected") || (selectedRows.length === 1 ? "KPI selected" : "KPIs selected")} · ${selectedGroups.length} ${label(selectedGroups.length === 1 ? "ncr-report-will-create" : "ncr-reports-will-create") || (selectedGroups.length === 1 ? "NCR report will be created" : "NCR reports will be created")}`}
                  {hiddenSelectedCount > 0
                    ? ` · ${label("ncr-hidden-selected-kpis") || "Selected KPIs hidden by this filter"}: ${hiddenSelectedCount}`
                    : ""}
                </Typography>
              )}
              {!audit ? (
                <Typography variant="body2" color="error" role="status">
                  {label("ncr-select-audit-to-view") || "Select an audit to see available NCR details."}
                </Typography>
              ) : !visibleRows.length ? (
                <Typography variant="body2">
                  {label("none-found") || "None found"}
                </Typography>
              ) : null}
              <NcrReportGroups
                groups={groups}
                visibleRows={visibleRows}
                loading={loading}
                toggleRow={toggleRow}
                groupRows={groupRows}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={handleClose}>
          {label("cancel-button")}
        </Button>
        <LoadingButton
          loading={loading}
          disabled={
            loading ||
            !audit ||
            !selectedRows.length ||
            hasNcrHeaderErrors(ncr) ||
            selectedRowsHaveErrors
          }
          type="submit"
          variant="gradient"
          color="info"
        >
          {`${label("create-button") || "Create"} ${selectedGroups.length} ${label(selectedGroups.length === 1 ? "ncr-report-singular" : "ncr-report-plural") || (selectedGroups.length === 1 ? "NCR report" : "NCR reports")}`}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
